/**
 * The discount of a billing cycle longer than a month, for any product. The API sends no discount,
 * so it is read off the prices: what the same configuration costs month by month over the cycle
 * against what the cycle bills. Callers sum their own plan and chosen options into the two prices.
 */

/** What a billing cycle saves against paying month by month. */
export interface CycleDiscount {
  /** Whole percent, floor-rounded - the design's "~" in "Save ~5%" and "You save (~5%)". */
  percent: number
  /** The saved amount over the whole cycle, rounded to cents. */
  amount: number
}

/** A billing cycle's length in months; 0 for a unit that is not months or years. */
export const monthsIn = (cycle: { measurement: string; qty: number }): number => {
  const unit = cycle.measurement.toLowerCase()
  if (unit.startsWith('month')) return cycle.qty
  if (unit.startsWith('year')) return cycle.qty * 12
  return 0
}

/**
 * `monthlyPrice` x `months` against `cyclePrice`, e.g. 12 x 85 = 1020 against 969: 51 saved, ~5%.
 * Returns null when the cycle saves under 1% (no flag, no "You save" row), and for a cycle of one
 * month or less.
 */
export const calculateCycleDiscount = (
  monthlyPrice: number,
  cyclePrice: number,
  months: number,
): CycleDiscount | null => {
  const undiscounted = monthlyPrice * months
  if (months <= 1 || !(undiscounted > 0)) return null
  const amount = Math.round((undiscounted - cyclePrice) * 100) / 100
  // The epsilon keeps an exact 5% from flooring to 4 on float noise.
  const percent = Math.floor((amount * 100) / undiscounted + 1e-9)
  return percent >= 1 ? { amount, percent } : null
}
